import {gql} from '@apollo/client';
import {groupByUniqueKey, objectFromEntries, entries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useReducer, useMemo} from 'react';

import {assert} from 'Asserts';
import {SequencePrime} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP} from 'constants/SequenceConstants';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  SchemaCompatibilityQuery,
  SchemaCompatibilityQueryVariables,
} from 'molbio/bulk-assembly/assemble-modal/__generated/SchemaCompatibilityQuery';
import PerformAssemblyDefineOutputsPane from 'molbio/bulk-assembly/assemble-modal/PerformAssemblyDefineOutputsPane';
import performAssemblyModalReducer, {
  getInitialReducerState,
  PerformAssemblyModalState,
  PerformAssemblyModalAction,
  PerformAssemblyModalStep,
} from 'molbio/bulk-assembly/assemble-modal/performAssemblyModalReducer';
import {
  FragmentDetails,
  RawOutputPolymerCount,
  UnknownInputPolymers,
  mergeAssemblyEntityCountsByModelType,
  getTotalCountFromAssemblyEntityCountsByModelType,
  AssemblyEntityCountsByModelType,
} from 'molbio/bulk-assembly/assemble-modal/polymerSchemaLinkCompatibility';
import {BinDetails} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {FinalizationLocationInfo, SaveInformation} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {getSequenceSubtype} from 'molbio/SeqUtil';
import {TableSelection, TableSelectionType} from 'notebook/structured-tables/components/EntryTableDropdown';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import * as Colors from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';
import {capitalize} from 'util/NameUtil';

import PerformAssemblyReviewAndFinalizePane from './PerformAssemblyReviewAndFinalizePane';

export const SCHEMA_COMPATIBILITY_QUERY = gql`
  query SchemaCompatibilityQuery($polymerIds: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    polymers(ids: $polymerIds) {
      ... on SequenceNode {
        id
        sequenceType
        nucleotideType
        tagSchema {
          id
          interfaces {
            id
          }
        }
      }
      ... on ProteinNode {
        id
        tagSchema {
          id
          interfaces {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getAdjustedInputDetailsByModelType(
  inputDetails: UnknownInputPolymers,
  schemaCompatibilityResult: {data: SchemaCompatibilityQuery}
): AssemblyEntityCountsByModelType {
  const accessiblePolymersById = groupByUniqueKey(
    accessibleObjs(schemaCompatibilityResult.data.polymers),
    ({id}) => id
  );
  const singleCountsByModelType = inputDetails.polymerIds.map((polymerId: string) => {
    const polymerDetails = accessiblePolymersById[polymerId];
    if (polymerDetails == null) {
      throw new Error('Encountered unreadable polymer');
    }
    let modelType: OligoAwareFolderItemType;
    if (polymerDetails.__typename === 'SequenceNode') {
      modelType = getSequenceSubtype(
        SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[polymerDetails.sequenceType],
        polymerDetails.nucleotideType
      );
    } else {
      modelType = ModelTypeConstants.PROTEIN;
    }

    if (polymerDetails.tagSchema != null) {
      return {
        [modelType]: {
          unschematizedPolymerCounts: 0,
          polymerCountsBySchemaId: {
            [polymerDetails.tagSchema.id]: 1,
          },
        },
      };
    } else {
      return {
        [modelType]: {
          unschematizedPolymerCounts: 1,
          polymerCountsBySchemaId: {},
        },
      };
    }
  });
  return mergeAssemblyEntityCountsByModelType(singleCountsByModelType);
}

function getAdjustedOutputDetailsByModelType(
  outputDetails: RawOutputPolymerCount,
  tagSchemaId: string | null
): AssemblyEntityCountsByModelType {
  if (!tagSchemaId) {
    return {
      [outputDetails.modelType]: {
        unschematizedPolymerCounts: outputDetails.count,
        polymerCountsBySchemaId: {},
      },
    };
  } else {
    return {
      [outputDetails.modelType]: {
        unschematizedPolymerCounts: 0,
        polymerCountsBySchemaId: {
          [tagSchemaId]: outputDetails.count,
        },
      },
    };
  }
}

function _getSaveInformation(
  state: PerformAssemblyModalState,
  entityType: 'fragment' | 'construct' | 'primer',
  shouldSave: boolean
): SaveInformation | null {
  if (!shouldSave) {
    return null;
  }

  const locationInfo = state[`${entityType}LocationInfo`];
  const selectedTableKey = `selected${capitalize(entityType)}Table` as keyof PerformAssemblyModalState;
  const selectedTable = state[selectedTableKey] as TableSelection | null;

  let worklistOptions: SaveInformation['worklistOptions'];
  if (locationInfo.worklistParams?.newWorklistInfo?.name) {
    worklistOptions = {
      worklistDestination: 'new',
      newWorklistName: locationInfo.worklistParams.newWorklistInfo.name,
    };
  } else if (locationInfo.worklistParams?.targetWorklistId) {
    worklistOptions = {
      worklistDestination: 'existing',
      worklistId: locationInfo.worklistParams.targetWorklistId,
    };
  }

  let tableOptions: SaveInformation['tableOptions'];
  if (selectedTable) {
    if (selectedTable.type === TableSelectionType.NEW) {
      tableOptions = {
        tableDestination: 'new',
        newTableName: selectedTable.name,
        tagSchemaId: locationInfo.tagSchemaId!,
      };
    } else if (selectedTable.type === TableSelectionType.EXISTING) {
      tableOptions = {
        tableDestination: 'existing',
        structuredTableId: selectedTable.table.apiId,
        tagSchemaId: locationInfo.tagSchemaId!,
      };
    }
  }

  return {
    folderId: locationInfo.targetFolderId!,
    tagSchemaId: locationInfo.tagSchemaId,
    worklistOptions,
    fieldMapping: locationInfo.fieldMapping,
    documentLikeId: state.addOutputsToEntry ? (state.selectedEntryId ?? undefined) : undefined,
    tableOptions,
  };
}

function getStepButtons({
  state,
  dispatch,
  onRequestSubmit,
}: {
  state: PerformAssemblyModalState;
  dispatch: React.Dispatch<PerformAssemblyModalAction>;
  onRequestSubmit: (saveInformation: FinalizationLocationInfo) => void;
}): Array<ModalButton> {
  const errors = state.currentStepValidationErrors;
  const disableNextButton = errors.length > 0;

  const onClickAssemble = (): void => {
    assert(
      (state.fragmentLocationInfo.targetFolderId != null || !state.saveFragments) &&
        state.constructLocationInfo.targetFolderId != null &&
        (state.primerLocationInfo.targetFolderId != null || !state.savePrimers),
      'Expected fragment, construct, and primer folders to be specified when saving those entities'
    );

    onRequestSubmit({
      fragmentSaveInfo: _getSaveInformation(state, 'fragment', state.saveFragments),
      constructSaveInfo: _getSaveInformation(state, 'construct', true)!,
      primerSaveInfo: _getSaveInformation(state, 'primer', state.savePrimers),
    });
  };

  const assembleButton = {
    text: 'Assemble',
    submit: true,
    disabled: disableNextButton,
    tooltipTitle: disableNextButton ? errors[0] : undefined,
    onClick: onClickAssemble,
  };
  if (state.stepKey === PerformAssemblyModalStep.REVIEW_AND_FINALIZE) {
    return [
      {
        text: 'Back',
        onClick: () => {
          dispatch({type: 'SET_STEP_KEY', value: PerformAssemblyModalStep.DEFINE_OUTPUTS});
          dispatch({
            type: 'SET_STATUS_BY_STEP',
            value: {
              ...state.statusByStep,
              [PerformAssemblyModalStep.REVIEW_AND_FINALIZE]: StepItemStatus.UNVISITED,
            },
          });
        },
      },
      assembleButton,
    ];
  } else {
    return [
      {text: 'Cancel', cancel: true},
      {
        text: 'Next',
        submit: true,
        disabled: disableNextButton,
        tooltipTitle: disableNextButton ? errors[0] : undefined,
        dataTestAttrs: {component: 'PerformAssemblyModal', key: ModalButtonKey.NEXT},
        onClick: () => {
          if (state.statusByStep[state.stepKey] !== StepItemStatus.ERROR) {
            dispatch({
              type: 'SET_STATUS_BY_STEP',
              value: {
                ...state.statusByStep,
                [PerformAssemblyModalStep.DEFINE_OUTPUTS]: StepItemStatus.DONE,
              },
            });
          }
          dispatch({type: 'SET_STEP_KEY', value: PerformAssemblyModalStep.REVIEW_AND_FINALIZE});
        },
      },
    ];
  }
}

export interface PerformAssemblyModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (saveInformation: FinalizationLocationInfo) => void;
  currentUserId: string;
  canCreatePrimers: boolean;
  canCreateFragments: boolean;
  constructOutputDetails: RawOutputPolymerCount;
  primerOutputDetails: RawOutputPolymerCount;
  orderedBinDetails: Array<BinDetails>;
  fragmentDetailsByBin: Record<string, FragmentDetails>;
  primerDetailsByPrime: Record<
    SequencePrime,
    {preferredPrimers: UnknownInputPolymers; designedPrimers: RawOutputPolymerCount}
  >;
  fragmentIcon: BenchlingIconName;
}

export default function PerformAssemblyModal(props: PerformAssemblyModalProps): JSX.Element {
  const [state, dispatch] = useReducer(
    performAssemblyModalReducer,
    getInitialReducerState(
      props.initialFolderId,
      props.canCreatePrimers && props.primerOutputDetails.count > 0
    )
  );

  const inputPolymerIds = useMemo(() => {
    const inputFragmentPolymerIds = Object.values(props.fragmentDetailsByBin).flatMap(
      (binDetails) => binDetails.inputDetails.polymerIds
    );
    const preferredPrimerIds = Object.values(props.primerDetailsByPrime).flatMap(
      (primeDetails) => primeDetails.preferredPrimers.polymerIds
    );
    return Array.from(new Set([...inputFragmentPolymerIds, ...preferredPrimerIds]));
  }, [props.fragmentDetailsByBin, props.primerDetailsByPrime]);

  const schemaCompatibilityResult = useBenchlingQuery<
    SchemaCompatibilityQuery,
    SchemaCompatibilityQueryVariables
  >(SCHEMA_COMPATIBILITY_QUERY, {
    variables: {
      polymerIds: inputPolymerIds,
    },
  });

  const {adjustedFragmentDetailsByBin, totalOutputFragmentCount} = useMemo(() => {
    if (schemaCompatibilityResult.type !== QueryResultType.SUCCESS) {
      return {
        adjustedFragmentDetailsByBin: {},
        totalOutputFragmentCount: 0,
      };
    }

    const adjustedDetails = objectFromEntries(
      entries(props.fragmentDetailsByBin).map(([binId, {inputDetails, outputDetails}]) => [
        binId,
        {
          inputDetails: getAdjustedInputDetailsByModelType(inputDetails, schemaCompatibilityResult),
          outputDetails: getAdjustedOutputDetailsByModelType(
            outputDetails,
            state.selectedFragmentBinId == null || state.selectedFragmentBinId === binId
              ? state.fragmentLocationInfo.tagSchemaId
              : null
          ),
        },
      ])
    );

    const mergedOutputFragmentDetails = props.canCreateFragments
      ? mergeAssemblyEntityCountsByModelType(
          props.orderedBinDetails.map((bin) => adjustedDetails[bin.binId].outputDetails)
        )
      : {};

    const totalCount = getTotalCountFromAssemblyEntityCountsByModelType(mergedOutputFragmentDetails);

    return {
      adjustedFragmentDetailsByBin: adjustedDetails,
      totalOutputFragmentCount: totalCount,
    };
  }, [
    schemaCompatibilityResult,
    props.fragmentDetailsByBin,
    props.orderedBinDetails,
    props.canCreateFragments,
    state.selectedFragmentBinId,
    state.fragmentLocationInfo.tagSchemaId,
  ]);

  if (schemaCompatibilityResult.type === QueryResultType.ERROR) {
    throw new Error('Error loading schema compatibility');
  } else if (schemaCompatibilityResult.type === QueryResultType.PENDING) {
    return (
      <Modal
        title="Finalize assembly"
        buttons={[{text: 'Cancel', cancel: true}]}
        onRequestClose={props.onRequestClose}
        mediumLarge={true}
        bodyPadded={true}
        showHeaderBorder={false}
      >
        <div style={{textAlign: 'center', padding: '40px'}}>Loading...</div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Finalize assembly"
      buttons={getStepButtons({
        state,
        dispatch,
        onRequestSubmit: props.onRequestSubmit,
      })}
      onRequestClose={props.onRequestClose}
      mediumLarge={true}
      bodyPadded={false}
      showHeaderBorder={false}
    >
      <div className={css(styles.stepNavContainer)}>
        <StepNav<PerformAssemblyModalStep>
          steps={[
            {
              key: PerformAssemblyModalStep.DEFINE_OUTPUTS,
              title: 'Define outputs',
            },
            {key: PerformAssemblyModalStep.REVIEW_AND_FINALIZE, title: 'Review and finalize'},
          ]}
          statusByStep={state.statusByStep}
          currentStepKey={state.stepKey}
          onUpdateCurrentStep={(step: PerformAssemblyModalStep) => {
            dispatch({type: 'SET_STEP_KEY', value: step});
          }}
          variant={StepNavVariant.FLAT}
        />
      </div>
      <hr className={css(styles.fullWidthDivider)} />

      {state.stepKey === PerformAssemblyModalStep.DEFINE_OUTPUTS && (
        <PerformAssemblyDefineOutputsPane
          orderedBinDetails={props.orderedBinDetails}
          constructOutputDetails={props.constructOutputDetails}
          primerOutputDetails={props.primerOutputDetails}
          canCreatePrimers={props.canCreatePrimers}
          canCreateFragments={props.canCreateFragments}
          fragmentDetailsByBin={props.fragmentDetailsByBin}
          fragmentIcon={props.fragmentIcon}
          primerDetailsByPrime={props.primerDetailsByPrime}
          dispatch={dispatch}
          state={state}
          schemaCompatibilityResult={schemaCompatibilityResult}
          adjustedFragmentDetailsByBin={adjustedFragmentDetailsByBin}
          totalOutputFragmentCount={totalOutputFragmentCount}
        />
      )}

      {state.stepKey === PerformAssemblyModalStep.REVIEW_AND_FINALIZE && (
        <PerformAssemblyReviewAndFinalizePane
          state={state}
          orderedBinDetails={props.orderedBinDetails}
          dispatch={dispatch}
          fragmentIcon={props.fragmentIcon}
          constructOutputCount={props.constructOutputDetails.count}
          primerOutputCount={props.primerOutputDetails.count}
          totalOutputFragmentCount={totalOutputFragmentCount}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  stepNavContainer: {
    padding: '0 16px 12px',
  },
  modalBody: {
    padding: '16px',
  },
  fullWidthDivider: {
    border: 0,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    margin: 0,
    width: '100%',
  },
});
